import { useRef } from 'react'

export interface StickyRows<TRow> {
  /** Rows to render: the fetched ones, or the last non-empty set if a fetch failed. */
  rows: TRow[]
  /** False only before any page has ever arrived — the one case an error pane is right. */
  hasEverHadRows: boolean
  /**
   * True when `rows` is the kept-back page rather than what was asked for.
   *
   * Callers that key anything off the current page need this: the rows on
   * screen belong to the previous page, so recording them against the new
   * page's key makes a later successful load look like a refetch of it.
   */
  isShowingPreviousRows: boolean
}

/**
 * Keeps the last page of rows on screen when a later fetch fails.
 *
 * `placeholderData: keepPreviousData` does not cover this. The placeholder is
 * applied only while the new key's query is PENDING, so a filter or page change
 * that *errors* leaves `data` undefined and the grid empty — while a same-key
 * refetch failure keeps its rows from the cache. That difference is why the
 * blanking looks fixed when tested with the Refresh button and is not.
 *
 * Showing a stale page under a warning beats an error pane: the admin keeps the
 * rows they were reading, and the banner says they may be out of date. An error
 * pane is right only before anything has ever loaded, when there is nothing to
 * keep.
 */
export function useStickyRows<TRow>(
  fetchedRows: TRow[],
  error: unknown
): StickyRows<TRow> {
  const hasEverHadRowsRef = useRef(false)
  const lastNonEmptyRowsRef = useRef<TRow[]>([])

  if (fetchedRows.length > 0) {
    hasEverHadRowsRef.current = true
    lastNonEmptyRowsRef.current = fetchedRows
  }

  const isShowingPreviousRows = Boolean(error) && fetchedRows.length === 0

  return {
    rows: isShowingPreviousRows ? lastNonEmptyRowsRef.current : fetchedRows,
    isShowingPreviousRows,
    hasEverHadRows: hasEverHadRowsRef.current,
  }
}
